/* global globalThis */
/**
 * @fileoverview Top Opportunities Component
 * @module components/opportunities/TopOpportunities
 * @description A reusable table component for displaying medication opportunities with sorting,
 * filtering, and various display modes for different contexts (member monitoring, priority outreach).
 *
 * @requires react
 * @requires prop-types
 * @requires @mui/material
 * @requires @mui/icons-material
 * @requires date-fns
 * @requires lodash
 * @requires ../../utils/capitalize
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { placeholderText, successText } from "../../constants/colors";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import { formatDateOnly } from "../../utils/formatDateTime";
import BiosimilarsIcon from "../../assets/Biosimilars.svg";
import PAPIcon from "../../assets/PAP.svg";
import InternationalSourcingIcon from "../../assets/IntSourcing.svg";
import CopayAssistIcon from "../../assets/CoPayAssist.svg";
import ECMPIcon from "../../assets/ECMP.svg";
import GLP1Icon from "../../assets/GLP 1.svg";
import WomensHealthIcon from "../../assets/WomensHealth.svg";
import DiabetesIcon from "../../assets/Diabetes.svg";
import WeightManagementIcon from "../../assets/WeightManagement.svg";
import ScienceIcon from "@mui/icons-material/Science";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import FocusableScrollContainer from "../common/FocusableScrollContainer";

/**
 * Calculates the screen diagonal size in inches
 * @private
 * @returns {number} Screen diagonal size in inches
 */
function getScreenInches() {
  const { width, height } = globalThis.screen;
  const dpi = globalThis.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}
/**
 * Formats a date string to MM/DD/YYYY format, handling various input formats
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string in MM/DD/YYYY format
 */
const formatDate = (dateString) => {
  if (!dateString) {
    return "";
  }

  const dashFormatMatch = /^(\d{2})-(\d{2})-(\d{2})$/.exec(dateString);

  if (dashFormatMatch) {
    const day = dashFormatMatch[1];
    const month = dashFormatMatch[2];
    const year = "20" + dashFormatMatch[3];
    return `${month}/${day}/${year}`;
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month.toString().padStart(2, "0")}/${day
    .toString()
    .padStart(2, "0")}/${year}`;
};
/**
 * Parses a drug name to extract and format the name and strength components
 * @param {string} drugName - The raw drug name string
 * @returns {Object} Object containing formatted name and strength
 * @property {string} name - The formatted drug name
 * @property {string} strength - The extracted strength information
 */
/**
 * Generates an SVG color filter for dynamic icon coloring
 * @param {string} color - Hex color code (e.g., "#FF0000")
 * @returns {string} SVG filter URL for recoloring
 */
const getSvgFilter = (color) => {
  const hexToRgb = (hex) => {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  const [r, g, b] = hexToRgb(color);
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='recolor' color-interpolation-filters='sRGB'%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 ${r / 255
    } 0 0 0 0 ${g / 255} 0 0 0 0 ${b / 255
    } 0 0 0 1 0'/%3E%3C/filter%3E%3C/svg%3E#recolor")`;
};

/**
 * Returns color scheme based on status
 * @param {string} status - The status to get colors for
 * @returns {Object} Color scheme object
 * @property {string} bgColor - Background color
 * @property {string} borderColor - Border color
 * @property {string} iconColor - Icon/text color
 */

/**
 * Returns the appropriate icon for a given category with status-based theming
 * @param {string} category - The category name
 * @param {string} [status=''] - Optional status for color theming
 * @returns {JSX.Element} Icon component for the category
 */
const DEFAULT_ICON_COLOR = "#403C6F";
const DEFAULT_BG_COLOR = "#ECECF1";
const DEFAULT_BORDER_COLOR = "#ECECF1";

const getCategoryIcon = (category) => {
  const iconStyle = {
    width: "16px",
    height: "16px",
    marginRight: "4px",
    filter: getSvgFilter(DEFAULT_ICON_COLOR),
  };

  switch (category) {
    case "Biosimilars":
      return <img src={BiosimilarsIcon} alt="Biosimilars" style={iconStyle} />;
    case "PAP":
      return <img src={PAPIcon} alt="PAP" style={iconStyle} />;
    case "International Sourcing":
      return (
        <img
          src={InternationalSourcingIcon}
          alt="International Sourcing"
          style={iconStyle}
        />
      );
    case "Copay Assist":
      return <img src={CopayAssistIcon} alt="Copay Assist" style={iconStyle} />;
    case "ECMP":
      return <img src={ECMPIcon} alt="ECMP" style={iconStyle} />;
    case "GLP-1":
      return <img src={GLP1Icon} alt="GLP-1" style={iconStyle} />;
    case "Women's Health Program":
    case "Women's Health":
      return <img src={WomensHealthIcon} alt="Women's Health Program" style={iconStyle} />;
    case "Diabetes":
      return <img src={DiabetesIcon} alt="Diabetes" style={iconStyle} />;
    case "Weight Management":
    case "Weight":
      return <img src={WeightManagementIcon} alt="Weight Management" style={iconStyle} />;
    default:
      return (
        <ScienceIcon style={{ ...iconStyle, color: DEFAULT_ICON_COLOR }} />
      );
  }
};

/**
 * Gets status information including colors based on category and status
 * @param {string} category - The category of the opportunity
 * @param {string} status - The status of the opportunity
 * @param {string} [colourCode] - Optional colour code from API response
 * @returns {Object} Status information object
 */
const getStatusInfo = (category, status, colourCode) => {
  return {
    status: status || "Eligible",
    statusColor: colourCode || DEFAULT_BG_COLOR,
    statusText: DEFAULT_ICON_COLOR,
    borderColor: colourCode || DEFAULT_BORDER_COLOR,
    categoryColor: "#F3E5F5",
    categoryText: "#4A148C",
  };
};

const PRIORITY_OUTREACH_DATA = {
  memberCost: [],
  lastFilled: [],
};
/**
 * TopOpportunities Component
 *
 * A table component that displays medication opportunities with various display modes
 * and interactive features like sorting and filtering.
 *
 * @param {Object} props - Component props
 * @param {Array} [props.opportunities=[]] - Array of opportunity objects to display
 * @param {('memberMonitoring'|'priorityOutreach')} [props.source='memberMonitoring'] - Display mode
 * @param {boolean} [props.showOutcome=false] - Whether to show outcome column
 * @returns {JSX.Element} The rendered component
 *
 * @example
 * <TopOpportunities
 *   opportunities={opportunityData}
 *   source="memberMonitoring"
 *   showOutcome={true}
 * />
 */
const TopOpportunities = ({
  opportunities = [],
  source = "memberMonitoring",
  showOutcome = false,
}) => {
  const [containerScale, setContainerScale] = useState(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const programStatusOrder = {
    eligible: 1,
    enrolled: 2,
    resolved: 3,
    "not resolved": 4,
    declined: 5,
  };

  const transformedOpportunities = [...opportunities]
    .map((opp, index) => {
      const name = opp.drug || opp.medicationName || "N/A";

      const monitoringReason = opp.monitoringReason || "N/A";

      const memberCost = opp.memberCost || opp.cost || "$0.00";

      let lastFilled;
      if (opp.lastFilled) {
        lastFilled = formatDateOnly(opp.lastFilled, { timeZone: "UTC" });
      } else if (source === "priorityOutreach") {
        lastFilled =
          PRIORITY_OUTREACH_DATA.lastFilled[
          index % PRIORITY_OUTREACH_DATA.lastFilled.length
          ];
      } else {
        lastFilled = "N/A";
      }

      const status =
        opp.status === "ELIGIBLE"
          ? "Not Enrolled"
          : capitalizeFirstLetter(opp.status) || "";
      const statusInfo = getStatusInfo(opp.category, status, opp.colourCode);

      const transformedItem = {
        ...opp,
        name: name,
        supply: "",
        dateAdded: opp.dateAdded ? formatDate(opp.dateAdded) : "",
        monitoringReason: monitoringReason,
        memberCost: memberCost,
        isGraduated: opp.isGraduated,
        lastFilled: lastFilled,
        ...statusInfo,
        medication: opp.drug || opp.medicationName,
        strength:
          opp.strength && opp.strength !== "N/A"
            ? opp.strength
            : opp.program?.strength || "",
        cost: memberCost,
        lastFilledDate: lastFilled,

        Quantity: opp.metricQuantity || opp.quantity || null,
        daysSupply: (() => {
          if (opp.program?.daySupply === undefined) {
            if (opp.daysSupply === undefined) {
              return null;
            } else {
              return Number(opp.daysSupply);
            }
          } else {
            return Number(opp.program.daySupply);
          }
        })(),
        pdc: opp.pdc || null,
        sig: opp.sig || null,
        potencyUnit: opp.potencyUnit || "",
      };
      return transformedItem;
    })
    .sort((a, b) => {
      if (a.isGraduated !== b.isGraduated) {
        return a.isGraduated ? 1 : -1;
      }

      const statusA = (a.status || "").toLowerCase();
      const statusB = (b.status || "").toLowerCase();
      const orderA = programStatusOrder[statusA] || 999;
      const orderB = programStatusOrder[statusB] || 999;
      return orderA - orderB;
    });
  useEffect(() => {
    const inches = getScreenInches();
    if (inches < 14) {
      setContainerScale(0.9);
    } else {
      setContainerScale(1);
    }
  }, []);

  const columns = (() => {
    if (source === "priorityOutreach") {
      return ["Medication", "Member Cost", "Last Filled"];
    } else {
      return [
        "Medication",
        showOutcome ? "Outreach Reason" : "Monitoring Reason",
      ];
    }
  })();
  const totalColumns = columns.length;
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transform: `scale(${containerScale})`,
        transformOrigin: "top left",
        width: containerScale < 1 ? "111%" : "100%",
      }}
    >
      <FocusableScrollContainer
        label="Scrollable top opportunities table"
        height="100%"
        sx={{ flex: 1 }}
      >
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            flex: 1,
            overflowX: "auto",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: `${placeholderText} #f1f1f1`,
            "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
              backgroundColor: `${placeholderText} !important`,
              borderRadius: "4px",
              backgroundClip: "padding-box",
              border: `2px solid ${placeholderText}`,
              opacity: "1 !important",
              visibility: "visible !important",
            },
            "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover":
            {
              backgroundColor: `${placeholderText} !important`,
            },
          }}
        >
          <Table
            sx={{
              borderCollapse: "collapse",
              tableLayout: "fixed",
              width: "100%",
              minWidth: isMobile ? "600px" : "100%", // Force scroll on mobile
              "& .MuiTableCell-root": {
                borderBottom: "1px solid #E0E0E0",
              },
              "& .MuiTableHead-root .MuiTableCell-root": {
                borderBottom: "1px solid #E0E0E0",
                backgroundColor: "#fff",
                position: "sticky",
                top: 0,
                zIndex: 1,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((col, i) => (
                  <TableCell
                    key={col}
                    component="th"
                    scope="col"
                    align={i === 0 ? "left" : "center"}
                    sx={{
                      pl: i === 0 ? 4 : 1,
                      fontWeight: 500,
                      color: "#403C6F",
                      padding: "12px 8px",
                      whiteSpace: "nowrap",
                      borderBottom: "1px solid #E0E0E0",
                      textAlign: i === 0 ? "left" : "center",
                      minWidth: (() => {
                        if (col === "Medication") {
                          return source === "memberMonitoring"
                            ? "300px"
                            : "180px";
                        } else if (col === "Last Filled") {
                          return "100px";
                        } else {
                          return "200px";
                        }
                      })(),
                      width: (() => {
                        if (col === "Last Filled") {
                          return "100px";
                        } else if (col === "Medication") {
                          return isMobile ? "300px" : "60%";
                        } else {
                          return "auto";
                        }
                      })(),
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {transformedOpportunities.length > 0 ? (
                transformedOpportunities.map((item, index) => (
                  <React.Fragment key={item.id || index}>
                    <TableRow
                      sx={{
                        verticalAlign: "top",
                        borderBottom: "1px solid #E0E0E0",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                        "&.MuiTableRow-root": {
                          "&.Mui-selected": {
                            backgroundColor: "#FFFF",
                            "& .MuiTableCell-root": {
                              borderBottom: "1px solid #E0E0E0",
                              "&:first-of-type": {
                                borderTopLeftRadius: "8px",
                                borderBottomLeftRadius: "8px",
                              },
                              "&:last-child": {
                                borderTopRightRadius: "8px",
                                borderBottomRightRadius: "8px",
                              },
                            },
                          },
                        },
                      }}
                      selected={
                        item.category === "Biosimilars" &&
                        item.status === "Eligible"
                      }
                    >
                      <TableCell
                        sx={{
                          width: "auto",
                          maxWidth: "none",
                          minWidth: "350px",
                          overflow: "visible",
                          whiteSpace: "normal",
                          padding: "12px 8px",
                          borderBottom: "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "nowrap",
                            justifyContent: "flex-start",
                            gap: "8px",
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              bgcolor: item.statusColor || "#E5F7EF",
                              borderRadius: "16px",
                              p: "4px 8px",
                              border: `1px solid ${item.borderColor || "#B6E9D1"
                                }`,
                              height: "32px",
                              boxSizing: "border-box",
                              flexShrink: 0,
                              maxWidth: "70%",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                overflow: "hidden",
                              }}
                            >
                              {getCategoryIcon(item.category)}
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#403C6F",
                                  fontWeight: 500,

                                  lineHeight: "150%",
                                  letterSpacing: "0%",
                                  pl: 0.5,
                                  pr: 0.25,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                }}
                              >
                                {item.category || "N/A"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box>
                          <Box sx={{ mb: 0.5 }}>
                            <Typography
                              sx={{
                                fontFamily: '"Manrope", sans-serif',
                                fontWeight: 600,
                                fontSize: "18px",
                                lineHeight: "28px",
                                letterSpacing: "0%",
                                color: "#403C6F",
                                display: "flex",
                                flexDirection: "column",

                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline",
                                    flexShrink: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  {capitalizeFirstLetter(
                                    (
                                      item.medicationName ||
                                      item.drug ||
                                      ""
                                    ).toLowerCase(),
                                  )}

                                  {(item.strength ||
                                    item.program?.strength) && (
                                      <span
                                        style={{
                                          marginLeft: "4px",
                                          fontWeight: 500,
                                          color: "#66638C",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        | {item.strength || item.program.strength}
                                      </span>
                                    )}
                                </span>
                              </Box>

                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                {/* NDC code first */}
                                <Typography
                                  variant="body2"
                                  component="span"
                                  sx={{
                                    display: "block",
                                    color: "#66638C",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    // mr: 2,
                                    mb: 0.75,
                                    mt: 0.75,
                                  }}
                                >
                                  NDC:{" "}
                                  {item.medication?.ndcCode ||
                                    item.drugNdc ||
                                    "N/A"}
                                </Typography>
                                {item.dateAdded && (
                                  <>
                                    <span
                                      style={{
                                        color: "#C4C3D2",
                                        marginLeft: "4px",
                                      }}
                                    >
                                      |
                                    </span>

                                    <span
                                      style={{
                                        fontWeight: 400,
                                        color: "#66638C",
                                        fontSize: "14px",
                                        marginLeft: "4px",
                                      }}
                                    >
                                      Halo Date:{" "}
                                    </span>
                                    <span
                                      style={{
                                        fontWeight: 500,
                                        color: "#66638C",
                                        fontSize: "14px",
                                        marginLeft: "4px",
                                      }}
                                    >
                                      {formatDate(item.dateAdded)}
                                    </span>
                                  </>
                                )}
                              </Box>
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "4px",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  color: "#6B6B9A",
                                  fontSize: "14px",
                                  lineHeight: "16px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Quantity :{" "}
                                {(() => {
                                  if (item.metricQuantity || item.quantity) {
                                    const quantity =
                                      item.metricQuantity || item.quantity;
                                    const potencyUnit = item.potencyUnit
                                      ? ` ${item.potencyUnit}`
                                      : "";
                                    return `${quantity}${potencyUnit}`;
                                  } else {
                                    return "N/A";
                                  }
                                })()}
                              </Typography>
                              <span style={{ color: "#C4C3D2" }}> | </span>
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                  color: "#6B6B9A",
                                  fontSize: "14px",
                                  lineHeight: "16px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Days Supply:{" "}
                                {(() => {
                                  if (item.daysSupply === 0) {
                                    return 0;
                                  } else if (item.daysSupply == null) {
                                    return "N/A";
                                  } else {
                                    return item.daysSupply;
                                  }
                                })()}
                              </Typography>
                            </Box>

                            {(item.pdc || item.sig) && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                  alignItems: "center",
                                }}
                              >
                                {item.pdc && (
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{
                                      color: "#6B6B9A",
                                      fontSize: "12px",
                                      lineHeight: "16px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {item.pdc} PDC
                                  </Typography>
                                )}
                                {item.pdc && item.sig && (
                                  <span style={{ color: "#C4C3D2" }}> | </span>
                                )}
                                {item.sig && (
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{
                                      color: "#6B6B9A",
                                      fontSize: "12px",
                                      lineHeight: "16px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    SIG: {item.sig}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {columns[1] && (
                        <TableCell
                          align="center"
                          sx={{
                            color:
                              source === "memberMonitoring"
                                ? "#403C6F"
                                : successText,
                            fontWeight: 600,
                            fontSize: "14px",
                            lineHeight: "20px",
                            borderBottom: "1px solid #f0f0f0",
                            whiteSpace:
                              source === "memberMonitoring"
                                ? "normal"
                                : "nowrap",
                            textAlign: "center",
                            padding: "8px 4px",
                            minWidth: "auto",
                            width: "auto",
                            boxSizing: "border-box",
                            verticalAlign: "middle",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              textAlign: "center",
                              pr: 0,
                              minHeight: "40px",
                            }}
                          >
                            {(() => {
                              if (source === "memberMonitoring") {
                                return item.monitoringReason || "N/A";
                              } else if (item.memberCost || item.cost) {
                                return `$${(
                                  item.memberCost || item.cost
                                ).replace("$", "")}`;
                              } else {
                                return "N/A";
                              }
                            })()}
                          </Box>
                        </TableCell>
                      )}

                      {columns.includes("Last Filled") && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "8px 4px",
                            borderBottom: "none",
                            verticalAlign: "middle",
                            width: "100px",
                            minWidth: "100px",
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              color: "#403C6F",
                              fontWeight: 500,
                              fontSize: "14px",
                              lineHeight: "20px",
                              whiteSpace: "nowrap",
                              textAlign: "center",
                              width: "100%",
                              display: "block",
                              minHeight: "40px",
                            }}
                          >
                            {item.lastFilled || "N/A"}
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={totalColumns}
                    align="center"
                    sx={{ color: placeholderText, py: 3 }}
                  >
                    No opportunities available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </FocusableScrollContainer>
    </Box>
  );
};

TopOpportunities.propTypes = {
  opportunities: PropTypes.array,
  source: PropTypes.oneOf(["memberMonitoring", "priorityOutreach"]),
  showOutcome: PropTypes.bool,
};
TopOpportunities.defaultProps = {
  source: "memberMonitoring",
};
export default TopOpportunities;
